import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Container,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCriiptoVerify } from "@criipto/verify-react";
import { useUserRegistrationDao } from "./useUserRegistrationDao";
import { RegisteringUserDto } from "./RegisteringUser";
import { useComponentSizeFromDense } from "shared-ts-ui-responsive";
import { ContactSupport } from "firefly-my-chimney-ts-pages";
import { NavigationLessAppPage } from "shared-ts-ui-app-boot";
import { useLanguage } from "shared-ts-language";
import { useAppTheme } from "shared-ts-ui-app-theme-provider";
import { useSxMerge } from "shared-ts-mui";
import { useIsMobileWebView } from "shared-ts-utils-ua-parser";
import { If } from "shared-ts-ui-react";
import {
  RegisterUserFormValues,
  UserRegistrationForm,
} from "./UserRegistrationForm";
import { UserRegistrationState } from "./UserRegistrationState";
import { McUserDto } from "firefly-my-chimney-ts-user";
import { ExternalMobileAuth } from "shared-ts-ui-identity";

const initialRegisterUserFormValues: RegisterUserFormValues = {
  email: "",
  password: "",
  confirmPassword: "",
  consentBurnTimeIsSaved: true,
};

export interface RegisterUserProcessProps {
  sx?: SxProps;
}

export const RegisterUserProcess: FC<RegisterUserProcessProps> = (props) => {
  const componentSize = useComponentSizeFromDense();
  const { language, setLanguage } = useLanguage();
  const { themeName, setTheme } = useAppTheme();
  const { t } = useTranslation("my-chimney-ts-translations");
  const { t: tSharedIdentity } = useTranslation("shared-ts-ui-identity");
  const isMobileWebView = useIsMobileWebView();
  // Sticky flag to handle cases where external-browser query params are lost during the
  // registration journey (e.g. IdP redirects). If we ever detect the flags on entry, we
  // record it in localStorage and keep honoring it through to success.
  const [externalMobileAuthSticky, setExternalMobileAuthSticky] =
    useState(false);

  useEffect(() => {
    try {
      // Promote cookie fallback to localStorage if set
      if (
        ExternalMobileAuth.hasCookieFlag() &&
        !ExternalMobileAuth.hasLocalStorageFlag()
      ) {
        ExternalMobileAuth.setLocalStorageFlag();
      }

      const sticky = ExternalMobileAuth.hasLocalStorageFlag();
      setExternalMobileAuthSticky(sticky);
      console.debug(
        "[RegisterUserProcess] [DEBUG_LOG] Sticky externalMobileAuth (localStorage):",
        sticky
      );
    } catch (e) {
      console.warn(
        "[RegisterUserProcess] [DEBUG_LOG] Failed to access localStorage for externalMobileAuth",
        e
      );
    }
  }, []);

  const isExternalMobileBrowser = useMemo(() => {
    // Canonical decision: rely solely on the sticky in localStorage (with cookie promotion on mount)
    if (externalMobileAuthSticky) {
      console.debug(
        "[RegisterUserProcess] [DEBUG_LOG] isExternalMobileBrowser: true (sticky flag present)"
      );
      return true;
    }
    console.debug(
      "[RegisterUserProcess] [DEBUG_LOG] isExternalMobileBrowser: false (no sticky)"
    );
    return false;
  }, [externalMobileAuthSticky]);

  const {
    result: criiptoLoginResult,
    isLoading: isTokenLoading,
    isInitializing: isTokenInitializing,
  } = useCriiptoVerify();
  const token = (criiptoLoginResult as any)?.id_token;
  const userRegistrationDao = useUserRegistrationDao(token);

  const [registeringUser, setRegisteringUser] = useState<
    RegisteringUserDto | undefined
  >(undefined);

  const [getRegisteringUserFailed, setGetRegisteringUserFailed] =
    useState<boolean>(false);

  const [getRegisteringUserRetryCount, setGetRegisteringUserRetryCount] =
    useState<number>(0);

  const [isFetchingRegisteringUser, setIsFetchingRegisteringUser] =
    useState<boolean>(false);

  const [registrationState, setRegistrationState] =
    useState<UserRegistrationState>("initial");
  const [registeredUser, setRegisteredUser] = useState<McUserDto | undefined>();
  const [registrationErrorMessage, setRegistrationErrorMessage] = useState<
    string | undefined
  >(undefined);

  const [initialFormValues, setInitialFormValues] =
    useState<RegisterUserFormValues>(initialRegisterUserFormValues);

  useEffect(
    function getRegisteringUser() {
      const doGetRegisteringUser = async () => {
        if (userRegistrationDao) {
          try {
            setIsFetchingRegisteringUser(true);
            const registeringUser =
              await userRegistrationDao.getRegisteringUser();
            setIsFetchingRegisteringUser(false);
            setRegisteringUser(registeringUser);
            setInitialFormValues({
              email: registeringUser.email ?? "",
              password: "",
              confirmPassword: "",
              consentBurnTimeIsSaved: true,
            });
          } catch (e) {
            setIsFetchingRegisteringUser(false);
            setGetRegisteringUserFailed(true);
          }
        }
      };
      doGetRegisteringUser();
    },
    [getRegisteringUserRetryCount, userRegistrationDao]
  );

  const handleRegistrationStateChange = (
    newState: UserRegistrationState,
    error?: string,
    user?: McUserDto
  ) => {
    setRegistrationState(newState);
    setRegistrationErrorMessage(error);
    if (user != null) {
      setRegisteredUser(user);
    }
  };

  const handleRetryGetRegisteredUser = () => {
    setGetRegisteringUserRetryCount((prevState) => prevState + 1);
  };

  const handleTokenExpiredTryAgain = () => {
    window.location.href = "/";
  };

  const handleGotoLogin = () => {
    window.location.assign("/");
  };

  const handleContinueInApp = () => {
    try {
      console.debug(
        "[RegisterUserProcess] Navigating to /auth-callback for mobile app handoff"
      );
      // Reuse the existing mobile auth-callback pipeline, which will emit
      // minpipe://auth-callback?token=... and hand control back to the
      // native app. The app then invokes /mobile/bootstrap-session to
      // finalize login in the WebView.
      window.location.href = "/auth-callback";
    } catch (e) {
      console.error(
        "[RegisterUserProcess] Failed to navigate to /auth-callback, falling back to /:",
        e
      );
      window.location.assign("/");
    }
  };

  const sx = useSxMerge(props.sx, {
    pt: 2,
    pb: 2,
  });

  return (
    <NavigationLessAppPage
      language={language}
      onLanguageChange={setLanguage}
      themeName={themeName}
      onThemeChange={setTheme as React.Dispatch<React.SetStateAction<string>>}
      flexGrowHeader={"auto"}
      sx={sx}
    >
      <Container
        maxWidth={"xs"}
        style={{
          flexGrow: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Stack
          className={"RegisterUser"}
          direction={"column"}
          useFlexGap
          gap={2}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            justifyContent: "start",
          }}
        >
          {(isTokenInitializing ||
            isTokenLoading ||
            isFetchingRegisteringUser) && (
            <div style={{ textAlign: "center" }}>
              <CircularProgress color="inherit" />
            </div>
          )}
          <>
            {token !== undefined && (
              <>
                {registeringUser && (
                  <>
                    <Typography variant={"h4"} align={"center"}>
                      {t("Welcome to My Chimney")}
                    </Typography>
                    <Typography variant={"h5"} align={"center"} sx={{ mb: 2 }}>
                      {registeringUser?.firstName +
                        " " +
                        registeringUser?.lastName}
                    </Typography>
                  </>
                )}
                {registrationState !== "success" && !isMobileWebView && (
                  <UserRegistrationForm
                    token={token}
                    initialFormValues={initialFormValues}
                    onRegistrationStateChange={handleRegistrationStateChange}
                  />
                )}
              </>
            )}
            {registrationState === "success" && (
              <>
                <Typography variant={"h5"} align={"center"}>
                  {tSharedIdentity("Your user is registered")}:
                </Typography>
                <Typography align={"center"}>
                  {registeredUser?.userName ?? "?"}
                </Typography>

                <Typography variant={"h6"} align={"center"}>
                  {tSharedIdentity(
                    "Please confirm your e-mail, an email with instructions is sent to {{email}}",
                    { email: registeredUser?.email ?? "?" }
                  )}
                </Typography>
                <If
                  expression={isExternalMobileBrowser}
                  then={
                    <Button
                      onClick={() => {
                        console.debug(
                          "[RegisterUserProcess] [DEBUG_LOG] Success branch: external → navigating to /auth-callback"
                        );
                        handleContinueInApp();
                      }}
                    >
                      {tSharedIdentity("Continue in app")}
                    </Button>
                  }
                  else={
                    <Stack useFlexGap sx={{ gap: 1, alignItems: "center" }}>
                      <Alert severity={"info"} variant={"outlined"}>
                        {tSharedIdentity("You can safely close this page")}
                      </Alert>
                      {tSharedIdentity("or")}
                      <Button
                        onClick={() => {
                          console.log(
                            "[RegisterUserProcess] [DEBUG_LOG] Success branch: normal browser → navigating to /"
                          );
                          handleGotoLogin();
                        }}
                        variant={"outlined"}
                      >
                        {tSharedIdentity("Goto Login")}
                      </Button>
                    </Stack>
                  }
                />
              </>
            )}
            {getRegisteringUserFailed && (
              <>
                <Alert severity={"error"}>
                  {`${tSharedIdentity(
                    "Sorry, we could not retrieve your user details"
                  )}. ${tSharedIdentity("Please try again")}.`}
                </Alert>
                <Button
                  variant={"contained"}
                  size={componentSize}
                  onClick={handleRetryGetRegisteredUser}
                  sx={{
                    alignSelf: "center",
                  }}
                >
                  {tSharedIdentity("Try again")}
                </Button>
              </>
            )}
            {registrationState === "error" && (
              <Alert severity={"error"}>
                <AlertTitle>
                  {tSharedIdentity("User registration failed")}
                </AlertTitle>
                {registrationErrorMessage != null && registrationErrorMessage}
              </Alert>
            )}
            {token === undefined &&
              !(isTokenInitializing || isTokenLoading) && (
                <>
                  <Alert severity={"error"} variant={"outlined"}>
                    {tSharedIdentity("Token for registration has expired")}
                  </Alert>
                  <Stack
                    direction={"row"}
                    sx={{ width: "100%", justifyContent: "space-evenly" }}
                  >
                    <Button
                      size={componentSize}
                      variant={"contained"}
                      onClick={handleTokenExpiredTryAgain}
                    >
                      {tSharedIdentity("Try again")}
                    </Button>
                  </Stack>
                </>
              )}
            {registrationState === "error" && (
              <ContactSupport
                issue={tSharedIdentity("Register a user").toLowerCase()}
              />
            )}
          </>
        </Stack>
      </Container>
    </NavigationLessAppPage>
  );
};
